import React, { useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { formatAge, ageInYears, getSacramentFormKind, getRegistrantLabelKey } from "../../../helper/ageHelper";
import { postSacramentDraft } from "../../../helper/sacramentSubmit";
import axiosInstance from "../../../hooks/api";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NasHeader from "../../layouts/_nas/NasHeader";
import NasFooter from "../../layouts/_nas/NasFooter";
import Breadcrumbs from "../../layouts/Breadcrumbs";
import NasBaptismForm from "../../sections/_nas/NasBaptismForm";
import NasChildrenRegForm from "../../sections/_nas/sacrament/NasChildrenRegForm";
import NasAdultRegForm from "../../sections/_nas/sacrament/NasAdultRegForm";

const MAX_PEOPLE = 4;

const createPerson = (id) => ({
  id,
  dateValue: null,
  birthDate: null,
  age: null,
  status: "pending",
});

const allocateFamilyGroupId = async () => {
  const res = await axiosInstance.post("/public/sacrament-family-groups");
  return res.data?.sibling_group_id || null;
};

const ageBadgeStyle = {
  background: "#e8f0fb",
  padding: "10px 18px",
  borderRadius: "6px",
  color: "#032C65",
  fontWeight: 600,
};

const NasSacramentPage = ({ type }) => {
  const { t, i18n } = useTranslation();
  const [count, setCount] = useState(1);
  const [people, setPeople] = useState([createPerson(1)]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [formSession, setFormSession] = useState(0);
  const draftsRef = useRef({});

  const multiFlow = count > 1;

  const allDatesReady = useMemo(
    () => people.every((p) => p.birthDate && p.age !== null),
    [people]
  );

  const completedCount = useMemo(
    () => people.filter((p) => p.status === "done").length,
    [people]
  );

  const advanceAfterComplete = (updated, index) => {
    const nextPending = updated.findIndex(
      (p, i) => i > index && p.status !== "done"
    );
    const anyPending = updated.findIndex((p) => p.status !== "done");
    const nextIndex =
      nextPending >= 0 ? nextPending : anyPending >= 0 ? anyPending : index;
    queueMicrotask(() => setActiveIndex(nextIndex));
  };

  const handleCountChange = (nextCount) => {
    const n = Math.min(MAX_PEOPLE, Math.max(1, Number(nextCount) || 1));
    setCount(n);
    draftsRef.current = {};
    setPeople((prev) => {
      if (n === prev.length) return prev;
      if (n > prev.length) {
        const extras = [];
        for (let i = prev.length; i < n; i += 1) {
          extras.push(createPerson(i + 1));
        }
        return [...prev, ...extras];
      }
      return prev.slice(0, n).map((p) => ({ ...p, status: "pending" }));
    });
    setActiveIndex((idx) => Math.min(idx, n - 1));
    setFormSession((s) => s + 1);
  };

  const handleDobChange = (index, value) => {
    delete draftsRef.current[index];
    setPeople((prev) =>
      prev.map((person, i) => {
        if (i !== index) return person;
        if (!value || !value.isValid()) {
          return { ...person, dateValue: value, birthDate: null, age: null, status: "pending" };
        }
        const formatted = value.format("YYYY-MM-DD");
        return {
          ...person,
          dateValue: value,
          birthDate: formatted,
          age: ageInYears(formatted),
          status: "pending",
        };
      })
    );
  };

  const markPersonComplete = (index) => {
    setPeople((prev) => {
      const updated = prev.map((person, i) =>
        i === index ? { ...person, status: "done" } : person
      );
      advanceAfterComplete(updated, index);
      return updated;
    });
  };

  const handleContinue = (index, draft) => {
    draftsRef.current[index] = draft;
    markPersonComplete(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitBatch = async (index, draft) => {
    draftsRef.current[index] = draft;
    setIsBatchLoading(true);
    try {
      const ordered = people.map((_, i) => draftsRef.current[i]).filter(Boolean);
      const siblingGroupId = ordered.length > 1 ? await allocateFamilyGroupId() : null;
      for (const item of ordered) {
        await postSacramentDraft(
          siblingGroupId ? { ...item, siblingGroupId } : item
        );
      }
      setIsBatchLoading(false);
      await Swal.fire({
        title: t("_request_placed"),
        text: t("_registration_confirmation"),
        confirmButtonColor: "#032C65",
        confirmButtonText: t("_accept"),
        background: "#f7f8fa",
      });
      draftsRef.current = {};
      setPeople((prev) => prev.map((p) => ({ ...p, status: "pending" })));
      setActiveIndex(0);
      setFormSession((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setIsBatchLoading(false);
      console.error(err);
      Swal.fire({
        title: t("_oops"),
        text: t("_form_error_body"),
        icon: "error",
        confirmButtonColor: "#032C65",
        confirmButtonText: t("_accept"),
        background: "#f7f8fa",
      });
    }
  };

  const personLabel = (index) => {
    const person = people[index];
    const kind =
      person?.age !== null && person?.age !== undefined
        ? getSacramentFormKind(type, person.age)
        : null;
    return t(getRegistrantLabelKey(kind), { n: index + 1 });
  };

  const registrantCountLabel = t(`_registrant_count_${type}`, {
    defaultValue: t("_registrant_count_for", { sacrament: t(`_tab_${type}`) }),
  });
  const registrantCountHint = t(`_registrant_count_hint_${type}`, {
    defaultValue: t("_registrant_count_hint"),
  });

  const isFinalStepFor = (index) =>
    multiFlow && people.every((p, i) => i === index || p.status === "done");

  const buildCopySources = (currentIndex, kind) => {
    if (!multiFlow || !kind || kind === "not_applicable") return [];
    return people
      .map((_, i) => {
        const draft = draftsRef.current[i];
        if (i === currentIndex || !draft || draft.kind !== kind) return null;
        return {
          index: i,
          label: personLabel(i),
          data: draft.data || {},
        };
      })
      .filter(Boolean);
  };

  const multiProps = (index, kind) =>
    multiFlow
      ? {
          multiFlow: true,
          isFinalStep: isFinalStepFor(index),
          onContinue: (draft) => handleContinue(index, draft),
          onSubmitBatch: (draft) => handleSubmitBatch(index, draft),
          isBatchLoading,
          copySources: buildCopySources(index, kind),
        }
      : {
          onSuccess: () => markPersonComplete(index),
        };

  const renderFormForPerson = (person, index) => {
    const kind = getSacramentFormKind(type, person.age);
    const shared = multiProps(index, kind);

    if (kind === "infant_baptism") {
      return <NasBaptismForm birthDate={person.birthDate} {...shared} />;
    }
    if (kind === "not_applicable") {
      return (
        <div>
          <div
            className="alert"
            style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "6px",
              padding: "12px 16px",
            }}
          >
            <strong>{t("_age_not_applicable")}</strong>
          </div>
          {multiFlow && (
            <div className="d-grid mt-3">
              <button
                type="button"
                className="sigma_btn-custom light"
                style={{ padding: "14px", fontSize: "16px" }}
                disabled={isBatchLoading}
                data-testid={`skip-not-applicable-${index}`}
                onClick={() => {
                  if (isFinalStepFor(index)) {
                    handleSubmitBatch(index, { kind: "not_applicable", data: {} });
                  } else {
                    handleContinue(index, { kind: "not_applicable", data: {} });
                  }
                }}
              >
                {isFinalStepFor(index)
                  ? t("_submit_all_registrations")
                  : t("_continue_next_person")}
              </button>
            </div>
          )}
        </div>
      );
    }
    if (kind === "child") {
      return (
        <NasChildrenRegForm
          sacramentType={type}
          birthDate={person.birthDate}
          age={person.age}
          {...shared}
        />
      );
    }
    if (kind === "adult") {
      return (
        <NasAdultRegForm
          sacramentType={type}
          birthDate={person.birthDate}
          {...shared}
        />
      );
    }
    return null;
  };

  const tabStyle = (index) => {
    const isActive = index === activeIndex;
    const isDone = people[index]?.status === "done";
    return {
      border: "none",
      borderBottom: isActive ? "3px solid #032C65" : "3px solid transparent",
      background: "transparent",
      color: isDone ? "#198754" : "#032C65",
      fontWeight: isActive ? 700 : 500,
      padding: "10px 16px",
      cursor: "pointer",
      opacity: isActive ? 1 : 0.75,
    };
  };

  return (
    <>
      <MetaTags>
        <title>Holy Spirit Church | {t("_tab_" + type)}</title>
        <meta name="description" content="#" />
      </MetaTags>
      <NasHeader />
      <Breadcrumbs breadcrumb={{ pagename: t("_tab_" + type) }} />
      <section className="section">
        <div className="container">
          {type === "matrimony" ? (
            <NasAdultRegForm sacramentType="matrimony" birthDate={null} />
          ) : (
            <>
              <div className="mb-4">
                <label className="form-label fw-semibold d-block mb-2" id="registrant-count-label">
                  {registrantCountLabel} <em className="text-danger">*</em>
                </label>
                <div
                  className="d-flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-labelledby="registrant-count-label"
                  data-testid="registrant-count"
                >
                  {[1, 2, 3, 4].map((n) => {
                    const selected = count === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        data-testid={`registrant-count-${n}`}
                        onClick={() => handleCountChange(n)}
                        style={{
                          minWidth: "56px",
                          height: "48px",
                          padding: "0 18px",
                          borderRadius: "8px",
                          border: selected ? "2px solid #032C65" : "1px solid #c5d0e0",
                          background: selected ? "#032C65" : "#fff",
                          color: selected ? "#fff" : "#032C65",
                          fontWeight: 700,
                          fontSize: "18px",
                          lineHeight: 1,
                          cursor: "pointer",
                          boxShadow: selected
                            ? "0 2px 8px rgba(3, 44, 101, 0.25)"
                            : "none",
                          transition: "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                        }}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
                <small className="text-muted d-block mt-2">{registrantCountHint}</small>
              </div>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {people.map((person, index) => (
                  <div className="row g-3 mb-3" key={person.id}>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">
                        {t("_birth_date")} — {personLabel(index)}{" "}
                        <em className="text-danger">*</em>
                      </label>
                      <DatePicker
                        value={person.dateValue}
                        onChange={(value) => handleDobChange(index, value)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            inputProps: { "data-testid": `date-field-${index}` },
                          },
                        }}
                      />
                    </div>
                    {person.age !== null && (
                      <div className="col-sm-4 d-flex align-items-end">
                        <div style={ageBadgeStyle} data-testid={`age-badge-${index}`}>
                          {t("_age")}: {formatAge(person.birthDate, i18n.language)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </LocalizationProvider>

              {allDatesReady && (
                <>
                  {multiFlow && (
                    <div className="mb-3">
                      <div
                        className="d-flex flex-wrap align-items-center gap-2 mb-2"
                        role="tablist"
                        aria-label={t("_registrant_tabs")}
                      >
                        {people.map((person, index) => (
                          <button
                            key={person.id}
                            type="button"
                            role="tab"
                            aria-selected={index === activeIndex}
                            style={tabStyle(index)}
                            onClick={() => setActiveIndex(index)}
                            data-testid={`person-tab-${index}`}
                          >
                            {personLabel(index)}
                            {person.status === "done" ? ` ✓` : ""}
                          </button>
                        ))}
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }} data-testid="registration-progress">
                        {t("_registration_progress", {
                          done: completedCount,
                          total: count,
                        })}
                      </div>
                    </div>
                  )}

                  {people.map((person, index) => (
                    <div
                      key={`${person.id}-${person.birthDate}-${formSession}`}
                      style={{ display: index === activeIndex ? "block" : "none" }}
                      data-testid={`person-form-panel-${index}`}
                    >
                      {multiFlow && (
                        <p className="fw-semibold mb-3" style={{ color: "#032C65" }}>
                          {t("_registration_for", { person: personLabel(index) })}
                        </p>
                      )}
                      {renderFormForPerson(person, index)}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </section>
      <NasFooter />
    </>
  );
};

export default NasSacramentPage;
